import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const API_URL = process.env.REACT_APP_API_URL;

export type CompanyPolicy = {
  visibility: string;
  is_company_admin: boolean;
  can_manage_bank_accounts: boolean;
  can_approve_without_mpk: boolean
} | null;

export type AuthUser = {
  id: string;
  email: string;
  role: "admin" | "accountant" | "external";
  display_name?: string;
  companyPolicy?: CompanyPolicy;
};

type AuthContextType = {
  user: AuthUser | null;
  loading: boolean;
  authenticated: boolean;
  selectedCompany: string;
  setSelectedCompany: (id: string) => void;
  refreshUser: () => Promise<void>;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [selectedCompany, setSelectedCompanyState] = useState<string>("");

  const didInitRef = useRef(false);

  const setSelectedCompany = (id: string) => {
    setSelectedCompanyState(id);
    sessionStorage.setItem("selectedCompany", id);
  };

  const loadUser = useCallback(async (companyId?: string) => {
    const query = companyId ? `?companyId=${companyId}` : "";

    const res = await fetch(`${API_URL}/auth/me${query}`, {
      credentials: "include",
    });

    if (!res.ok) {
      setUser(null);
      return false;
    }

    const data = await res.json();
    setUser(data);
    return true;
  }, []);

  const refreshSession = useCallback(async () => {
    setLoading(true);

    try {
      const statusRes = await fetch(`${API_URL}/auth/status`, {
        credentials: "include",
      });

      if (!statusRes.ok) {
        setAuthenticated(false);
        setUser(null);
        return;
      }

      const statusData = await statusRes.json();

      if (!statusData.authenticated) {
        setAuthenticated(false);
        setUser(null);
        return;
      }

      // A valid session alone is not enough to render the authenticated app.
      // Confirm the local portal user first, without companyId, so a stale
      // selectedCompany value cannot block initialization with a policy 403.
      const userLoaded = await loadUser();

      if (!userLoaded) {
        setAuthenticated(false);
        setUser(null);
        return;
      }

      setAuthenticated(true);
    } catch {
      setAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadUser]);

  const refreshUser = useCallback(async () => {
    if (!authenticated) return;

    setLoading(true);
    try {
      await loadUser(selectedCompany || undefined);
    } finally {
      setLoading(false);
    }
  }, [authenticated, loadUser, selectedCompany]);

  useEffect(() => {
    const savedCompany = sessionStorage.getItem("selectedCompany") || "";
    if (savedCompany) {
      setSelectedCompanyState(savedCompany);
    }
  }, []);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    if (!authenticated) return;
    if (!selectedCompany) return;

    loadUser(selectedCompany).catch(() => {
      setUser(null);
    });
  }, [selectedCompany, authenticated, loadUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authenticated,
        selectedCompany,
        setSelectedCompany,
        refreshUser,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}